import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { smart_meters_base_url, customer_account_base_url } from "../../../../../../utils/urls";
import { toastify } from '../../../../../../utils/toast';
import Layout from '../../../../component/layout';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import AccountStatementGenerator from './accounts_statement_generator';
import { getItem } from '../../../../../../utils/localStorage';

function ViewPrepaidCustomer() {
    const navigate = useNavigate();
    const { meterId } = useParams();
    const location = useLocation();
    const accountId = location.state?.accountId;
    const toast = useRef(null);
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);
    const defaultCustomerData = {
        name: 'N/A',
        id: 'N/A',
        accountNumber: 'N/A',
        meterNumber: 'N/A',
        status: 'N/A',
        phone: 'N/A',
        userType: 'Prepaid',
        updatedAt: 'N/A',
        meter_id: 'N/A',
        unitName: 'N/A'
    };
    const [customerData, setCustomerData] = useState(defaultCustomerData);
    const [prepaidBalance, setPrepaidBalance] = useState(0.0);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [balanceLoading, setBalanceLoading] = useState(false);

    const [credits, setCredits] = useState([]);
    const [debits, setDebits] = useState([]);
    const [creditTotalRecords, setCreditTotalRecords] = useState(0);
    const [debitTotalRecords, setDebitTotalRecords] = useState(0);
    const [creditLazyParams, setCreditLazyParams] = useState({
        first: 0,
        rows: 5,
        page: 0,
    });
    const [debitLazyParams, setDebitLazyParams] = useState({
        first: 0,
        rows: 5,
        page: 0,
    });

    const [addCreditDialog, setAddCreditDialog] = useState(false);
    const [newCreditAmount, setNewCreditAmount] = useState(null);
    const [newCreditReason, setNewCreditReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Company details fetched from localStorage (set by Facilities component on facility selection)
    const [companyDetails, setCompanyDetails] = useState({
        name: '',
        address: '',
        country: 'Kenya',
        phone: '+254 709 000 505',
        email: 'support@payserve.co.ke'
    });

    // Fetch facility details from localStorage on mount
    useEffect(() => {
        const loadFacilityDetails = async () => {
            try {
                const selectedFacility = await getItem('selectedFacility');

                let facility;
                if (typeof selectedFacility === 'string') {
                    try {
                        facility = JSON.parse(selectedFacility);
                    } catch (err) {
                        console.error('Error parsing selectedFacility:', err);
                        return;
                    }
                } else {
                    facility = selectedFacility;
                }

                if (facility) {
                    setCompanyDetails(prev => ({
                        ...prev,
                        name: facility.name || prev.name,
                        address: facility.location || prev.address,
                    }));
                }
            } catch (error) {
                console.error('Error loading facility details:', error);
            }
        };

        loadFacilityDetails();
    }, []);

    useEffect(() => {
        if (accountId) {
            fetchCustomerDataById(accountId);
        } else if (meterId) {
            fetchCustomerDataByMeterId(meterId);
        }

    }, [accountId, meterId]);

    // Fetch balance when customer ID is available
    useEffect(() => {
        if (customerData.id !== 'N/A') {
            fetchPrepaidBalance(customerData.id);
        }
    }, [customerData.id]);

    // Fetch credits and debits when the customer account number is available
    useEffect(() => {
        if (customerData.id !== 'N/A' && selectedFacilityId) {
            fetchCredits();
            fetchDebits();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerData.id, creditLazyParams, debitLazyParams, selectedFacilityId]);

    const fetchCustomerDataById = async (id) => {
        try {
            setLoading(true);

            const response = await makeRequest2(
                `${customer_account_base_url}/get/${id}`,
                'GET',
                null
            );

            if (response.success) {
                const accountData = response.data.data;

                const customerDetails = {
                    id: accountData._id || accountData.id,
                    name: accountData.customerName || 'N/A',
                    accountNumber: accountData.account_no || 'N/A',
                    meterNumber: accountData.meterNumber || 'N/A',
                    status: accountData.status || 'N/A',
                    phone: accountData.phoneNumber || 'N/A',
                    userType: accountData.payment_type || 'Prepaid',
                    updatedAt: formatDateTime(accountData.updatedAt),
                    meter_id: accountData.meter_id || 'N/A',
                    unitName: accountData.unitName || 'N/A'
                };
                if (customerDetails.userType.toLowerCase() === 'postpaid') {
                    navigate(
                        '/facility/utility_management/meter_management/water_meters/view_customer_account',
                        { state: { accountId: customerDetails.id }, replace: true }
                    );
                    return;
                }
                setCustomerData(customerDetails);
            } else {
                toastify('Error fetching customer data', 'error');
            }
        } catch (error) {
            toastify('Error fetching customer data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomerDataByMeterId = async (meterId) => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `${smart_meters_base_url}/customer/${meterId}`,
                'GET',
                null
            );

            if (response.success) {
                const accountData = response.data;
                setCustomerData({
                    id: accountData._id || accountData.id,
                    name: accountData.customerName || 'N/A',
                    accountNumber: accountData.account_no || 'N/A',
                    meterNumber: accountData.meterNumber || 'N/A',
                    status: accountData.status || 'N/A',
                    phone: accountData.phoneNumber || 'N/A',
                    userType: accountData.payment_type || 'Prepaid',
                    updatedAt: formatDateTime(accountData.updatedAt),
                    meter_id: meterId,
                    unitName: accountData.unitName || 'N/A'
                });
            } else {
                toastify('Error fetching customer data', 'error');
            }
        } catch (error) {
            toastify('Error fetching customer data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchPrepaidBalance = async (accountId) => {
        try {
            setBalanceLoading(true);
            const response = await makeRequest2(
                `${customer_account_base_url}/get_accounts_balance/${accountId}`,
                'GET',
                null
            );

            if (response.success) {
                const balanceData = response.data.data || {};
                setPrepaidBalance(balanceData.currentBalance || 0.0);
            } else {
                setPrepaidBalance(0.0);
            }
        } catch (error) {
            setPrepaidBalance(0.0);
        } finally {
            setBalanceLoading(false);
        }
    };

    const fetchCredits = async () => {
        try {
            setLoading(true);

            const endpoint = `${customer_account_base_url}/get_accounts_credits/${customerData.id}`;

            const response = await makeRequest2(endpoint, 'GET');
            if (response.success) {

                const creditsData = response.data.data?.credits || [];
                setCredits(creditsData);
                setCreditTotalRecords(response.data.data?.totalCount || 0);
            } else {
                toastify('Failed to fetch credit transactions', 'error');
            }
        } catch (error) {
            toastify('Error loading credit transactions', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchDebits = async () => {
        try {
            setLoading(true);
            const endpoint = `${customer_account_base_url}/get_accounts_debits/${customerData.id}`;

            const response = await makeRequest2(endpoint, 'GET');
            if (response.success) {
                const debitsData = response.data.data?.debits || [];
                console.log(debits)
                setDebits(debitsData);
                setDebitTotalRecords(response.data.data?.totalCount || 0);
            } else {
                toastify('Failed to fetch debit transactions', 'error');
            }
        } catch (error) {
            toastify('Error loading debit transactions', 'error');
        } finally {
            setLoading(false);
        }
    };

    const onCreditPage = (event) => {
        setCreditLazyParams(event);
    };

    const onDebitPage = (event) => {
        setDebitLazyParams(event);
    };

    // Helpers for table formatting
    const formatNumber = (value) => {
        return new Intl.NumberFormat('en-KE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const amountBodyTemplate = (rowData) => {
        return `KES ${formatNumber(rowData.amount || 0)}`;
    };

    const totalAmountBodyTemplate = (rowData) => {
        return `KES ${formatNumber(rowData.totalAmount || 0)}`;
    };

    const consumptionBodyTemplate = (rowData) => {
        const formattedConsumption = formatNumber(rowData.consumption || 0);
        return `${formattedConsumption} units`;
    };

    const readingsBodyTemplate = (rowData) => {
        return `${rowData.prev_reading || 0} → ${rowData.current_reading || 0}`;
    };

    const formatDateTime = (dateString) => {
        if (!dateString || dateString === 'N/A') return 'N/A';

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A'; // Invalid date

            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');

            return `${year}-${month}-${day} ${hours}:${minutes}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'N/A';
        }
    };

    const exportCreditsCSV = () => {
        const header = [
            'Reference', 'Amount (KES)', 'Time', 'Date Added', 'Payment Type', 'Reason'
        ];

        const data = credits.map(credit => [
            credit.ref,
            credit.amount || 0,
            credit.time,
            credit.addedOn,
            credit.type,
            credit.reason || 'N/A'
        ]);

        downloadCSV(data, header, `Credits_${customerData.accountNumber}`);
    };

    const exportDebitsCSV = () => {
        const header = [
            'Previous Reading', 'Current Reading', 'Consumption', 'Rate',
            'Total Amount (KES)', 'Date', 'Time'
        ];

        const data = debits.map(debit => [
            debit.prev_reading || 0,
            debit.current_reading || 0,
            debit.consumption || 0,
            debit.rate,
            debit.totalAmount || 0,
            debit.date,
            debit.time
        ]);

        downloadCSV(data, header, `Debits_${customerData.accountNumber}`);
    };

    const downloadCSV = (data, header, filename) => {
        const csvContent = [
            header.join(','),
            ...data.map(row => row.map(cell =>
                typeof cell === 'string' && cell.includes(',')
                    ? `"${cell}"`
                    : cell
            ).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute(
            'download',
            `${filename}_${new Date().toISOString().slice(0, 10)}.csv`
        );
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Data exported to CSV', life: 3000 });
    };

    // Handle navigation back to the meter view page with meterId
    const handleNavigateBack = () => {

        const navigateMeterId = customerData.meter_id !== 'N/A'
            ? customerData.meter_id
            : meterId;
        navigate(
            `/facility/utility_management/meter_management/view_water_meter/${navigateMeterId}`,
            { state: { activeTab: "customer" } }
        );
    };

    const openAddDialog = () => {
        setNewCreditAmount(null);
        setNewCreditReason('');
        setAddCreditDialog(true);
    };

    const closeAddDialog = () => {
        setAddCreditDialog(false);
        setNewCreditAmount(null);
        setNewCreditReason('');
    };

    const generateReferenceNumber = () => {
        const randomDigits = Math.floor(100000 + Math.random() * 900000);
        return `CASH-${randomDigits}`;
    };

    const handleAddCredit = async () => {
        // Validation
        if (!newCreditAmount || newCreditAmount <= 0) {
            toastify('Please enter a valid amount', 'error');
            return;
        }

        if (!newCreditReason || newCreditReason.trim() === '') {
            toastify('Please provide a reason for adding credit', 'error');
            return;
        }

        try {
            setSubmitting(true);

            const now = new Date();
            const creditData = {
                accountId: customerData.id,
                amount: newCreditAmount,
                ref: generateReferenceNumber(),
                type: 'Manual',
                addedOn: now.toISOString().split('T')[0], // Format: YYYY-MM-DD
                time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
                reason: newCreditReason.trim()
            };

            // Make API request to add credit
            const response = await makeRequest2(
                `${customer_account_base_url}/add_credit/${customerData.accountNumber}`,
                'POST',
                creditData
            );

            if (response.success) {
                toastify('Credit added successfully', 'success');

                // Close dialog and refresh data
                closeAddDialog();
                fetchCredits();
                fetchPrepaidBalance(customerData.id);
            } else {
                toastify(response.error || 'Failed to add credit', 'error');
            }
        } catch (error) {
            console.error('Error adding credit:', error);
            toastify('Error adding credit', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const renderAddCreditDialog = () => {
        return (
            <Dialog
                header="Add Manual Credit"
                visible={addCreditDialog}
                style={{ width: '450px' }}
                onHide={closeAddDialog}
                modal
                className="p-fluid"
                footer={
                    <div>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={closeAddDialog}
                            disabled={submitting}
                        />
                        <Button
                            label="Add Credit"
                            icon="pi pi-check"
                            className="p-button-primary"
                            onClick={handleAddCredit}
                            loading={submitting}
                        />
                    </div>
                }
            >
                <div className="field">
                    <label htmlFor="amount">Amount (KES) <span className="text-danger">*</span></label>
                    <InputNumber
                        id="amount"
                        value={newCreditAmount}
                        onValueChange={(e) => setNewCreditAmount(e.value)}
                        mode="decimal"
                        minFractionDigits={2}
                        maxFractionDigits={2}
                        placeholder="Enter amount"
                        className="w-100"
                        min={0}
                    />
                </div>
                
                <div className="field">
                    <label htmlFor="reason">Reason <span className="text-danger">*</span></label>
                    <InputText
                        id="reason"
                        value={newCreditReason}
                        onChange={(e) => setNewCreditReason(e.target.value)}
                        placeholder="Enter reason for credit adjustment"
                        className="w-100"
                        maxLength={255}
                    />
                    <small className="text-muted">
                        Provide a clear reason for adding this credit (e.g., "Billing adjustment", "Goodwill credit", "Refund")
                    </small>
                </div>

                <div className="mt-3 p-2 bg-light-info rounded">
                    <p className="mb-0">
                        <i className="ti ti-info-circle me-1"></i>
                        A reference number will be automatically generated for this transaction.
                    </p>
                </div>
            </Dialog>
        );
    };

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/facility/">Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to="/facility/utility_management/meter_management/water_meters">
                                        Water Meters
                                    </Link>
                                </li>
                                <li className="breadcrumb-item active">
                                    <span>View Meter</span>
                                </li>
                                <li className="breadcrumb-item active">
                                    <span>Prepaid Account</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header">
                            <button
                                className="btn btn-link p-0 border-0"
                                onClick={handleNavigateBack}
                            >
                                <i className="ti ti-arrow-narrow-left"></i> Back
                            </button>
                        </div>
                    </div>
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <div>
                                <h3>Customer Account Details</h3>
                            </div>
                            <div>
                                {/* Account Statement Generator Button */}
                                <AccountStatementGenerator
                                    customerData={customerData}
                                    credits={credits}
                                    debits={debits}
                                    facilityLogo={null} // Add logo if available
                                    companyDetails={companyDetails}
                                    prepaidBalance={prepaidBalance}
                                />
                            </div>
                        </div>

                        {loading && customerData.id === 'N/A' ? (
                            <div className="card-body">
                                <div className="d-flex justify-content-center">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="card-body p-0">
                                <div className="row g-0">
                                    {/* Customer Info Panel - Left Side */}
                                    <div className="col-md-3 border-end">
                                        <div className="p-3">
                                            <div className="d-flex align-items-center mb-4">
                                                <div className="avatar avatar-xl bg-light-primary rounded-circle me-3">
                                                    <i className="ti ti-user f-22"></i>
                                                </div>
                                                <div>
                                                    <h4 className="mb-1">{customerData.name}</h4>
                                                    <span className={`badge ${customerData.status === 'Active' ? 'bg-success text-light' : 'bg-danger text-light'}`}>
                                                        {customerData.status}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="list-group list-group-flush">
                                                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                                                    <span className="text-muted">Account Type</span>
                                                    <span className="fw-bold">{customerData.userType}</span>
                                                </div>
                                                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                                                    <span className="text-muted">Account Number</span>
                                                    <span className="fw-bold">{customerData.accountNumber}</span>
                                                </div>
                                                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                                                    <span className="text-muted">Meter Number</span>
                                                    <span className="fw-bold">{customerData.meterNumber}</span>
                                                </div>
                                                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                                                    <span className="text-muted">Phone Number</span>
                                                    <span className="fw-bold">{customerData.phone}</span>
                                                </div>
                                                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                                                    <span className="text-muted">Unit Name</span>
                                                    <span className="fw-bold">{customerData.unitName}</span>
                                                </div>
                                                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                                                    <span className="text-muted">Last Updated</span>
                                                    <span className="fw-bold">{customerData.updatedAt}</span>
                                                </div>
                                            </div>

                                            <div className="mt-3 p-3 bg-light-success rounded">
                                                <h6 className="mb-2">Account Balance</h6>
                                                {balanceLoading ? (
                                                    <div className="d-flex justify-content-center">
                                                        <div className="spinner-border spinner-border-sm text-success" role="status">
                                                            <span className="visually-hidden">Loading balance...</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <h3 className="mb-0 text-success">
                                                            KES {typeof prepaidBalance === "number"
                                                                ? prepaidBalance.toFixed(2)
                                                                : "0.00"}
                                                        </h3>
                                                        <small className="text-muted mt-1 d-block">Current Balance</small>
                                                    </div>
                                                )}
                                                <button
                                                    className="btn btn-sm btn-outline-success mt-3"
                                                    onClick={() => fetchPrepaidBalance(customerData.id)}
                                                    disabled={balanceLoading}
                                                >
                                                    <i className="ti ti-refresh me-1"></i> Refresh Balance
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tabs Panel - Right Side */}
                                    <div className="col-md-9">
                                        <Toast ref={toast} />
                                        <TabView activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)} className="border-0">
                                            {/* Credits Tab */}
                                            <TabPanel header="Credits">
                                                <div className="p-3">
                                                    <div className="d-flex justify-content-end mb-3 gap-2">
                                                        <button
                                                            className="btn btn-outline-primary"
                                                            onClick={exportCreditsCSV}
                                                        >Export
                                                        </button>
                                                        <button
                                                            className="btn btn-primary"
                                                            onClick={openAddDialog}
                                                        > Add New Credit
                                                        </button>
                                                    </div>
                                                    <div className="dt-responsive table-responsive">
                                                        <DataTable
                                                            value={credits}
                                                            emptyMessage="No credit transactions found."
                                                            sortMode="multiple"
                                                            paginator
                                                            lazy
                                                            first={creditLazyParams.first}
                                                            rows={creditLazyParams.rows}
                                                            totalRecords={creditTotalRecords}
                                                            onPage={onCreditPage}
                                                            loading={loading}
                                                            stripedRows
                                                            tableStyle={{ minWidth: '50rem' }}
                                                        >
                                                            <Column field="ref" header="Reference" sortable />
                                                            <Column
                                                                field="amount"
                                                                header="Amount"
                                                                body={amountBodyTemplate}
                                                                sortable
                                                            />
                                                            <Column field="time" header="Time" sortable />
                                                            <Column field="addedOn" header="Date Added" sortable />
                                                            <Column
                                                                field="type"
                                                                header="Payment Type"
                                                                sortable
                                                            />
                                                            <Column 
                                                                field="reason" 
                                                                header="Reason" 
                                                                sortable 
                                                                body={(rowData) => rowData.reason || 'N/A'}
                                                            />
                                                        </DataTable>
                                                    </div>
                                                </div>
                                            </TabPanel>

                                            {/* Debits Tab */}
                                            <TabPanel header="Debits">
                                                <div className="p-3">
                                                    <div className="d-flex justify-content-end mb-3">
                                                        <button
                                                            className="btn btn-outline-primary"
                                                            onClick={exportDebitsCSV}
                                                        > Export
                                                        </button>
                                                    </div>
                                                    <div className="dt-responsive table-responsive">
                                                        <DataTable
                                                            value={debits}
                                                            emptyMessage="No debit transactions found."
                                                            sortMode="multiple"
                                                            paginator
                                                            lazy
                                                            first={debitLazyParams.first}
                                                            rows={debitLazyParams.rows}
                                                            totalRecords={debitTotalRecords}
                                                            onPage={onDebitPage}
                                                            loading={loading}
                                                            stripedRows
                                                            tableStyle={{ minWidth: '50rem' }}
                                                        >
                                                            <Column
                                                                header="Readings"
                                                                body={readingsBodyTemplate}
                                                            />
                                                            <Column
                                                                field="consumption"
                                                                header="Consumption"
                                                                body={consumptionBodyTemplate}
                                                                sortable
                                                            />
                                                            <Column field="rate" header="Rate" sortable />
                                                            <Column
                                                                field="totalAmount"
                                                                header="Total Amount"
                                                                body={totalAmountBodyTemplate}
                                                                sortable
                                                            />
                                                            <Column field="date" header="Date" sortable />
                                                            <Column field="time" header="Time" sortable />
                                                        </DataTable>
                                                    </div>
                                                </div>
                                            </TabPanel>
                                        </TabView>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {renderAddCreditDialog()}
        </Layout>
    );
}

export default ViewPrepaidCustomer;